<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import SlidingScrollableListingComponent from './SlidingScrollableListingComponent.vue';

// Props
const props = defineProps({
  favorites: { type: Array, default: () => [] },
});

// Emits
const emit = defineEmits(['viewDetails', 'toggleFavorite']);

// Store
const store = useStore();

// State
const debugMode = ref(process.env.NODE_ENV === 'development');

// ✅ SIMPLIFIED: Computed properties using store getters directly
const savedSearches = computed(() => store.getters['savedSearches/allSearches'] || []);
const displayListings = computed(() => store.getters['savedSearches/slidingDisplayListings'] || []);
const isLoading = computed(() => store.getters['savedSearches/isLoading']);
const error = computed(() => store.getters['savedSearches/errorMessage']);
//const shouldShowSection = computed(() => store.getters['savedSearches/shouldShowSection']);
const shouldShowSection = computed(() => {
  const result = store.getters['savedSearches/shouldShowSection'];
  return result;
});
const isAuthenticated = computed(() => store.getters['savedSearches/isUserAuthenticated']);

// ✅ ENHANCED: Dynamic title based on content type
const sectionTitle = computed(() => {
  const hasSearches = savedSearches.value.length > 0;
  const hasListings = displayListings.value.length > 0;

  if (hasSearches && hasListings) {
    return savedSearches.value.length === 1
      ? `Saved Search - ${savedSearches.value[0].name}`
      : `Saved Searches (${savedSearches.value.length})`;
  } else if (hasListings && !hasSearches) {
    return 'Recommended for You';
  } else if (hasSearches && !hasListings) {
    return 'Saved Searches';
  } else {
    return 'Discover Listings';
  }
});

// ✅ ENHANCED: Better breakdown calculation
const listingBreakdown = computed(() => {
  const savedCount = store.state.savedSearches?.savedSearchesListings?.length || 0;
  const similarCount = store.state.savedSearches?.similarListings?.length || 0;

  return {
    savedSearchResults: savedCount,
    similarListings: similarCount,
    hasSavedListings: savedCount > 0,
    hasSimilarListings: similarCount > 0,
    totalListings: savedCount + similarCount,
  };
});

// ✅ ENHANCED: Comprehensive debug info
const debugInfo = computed(() => ({
  // Core data
  savedSearchesCount: savedSearches.value.length,
  displayListingsCount: displayListings.value.length,

  // State info
  isLoading: isLoading.value,
  error: error.value,
  isAuthenticated: isAuthenticated.value,
  shouldShowSection: shouldShowSection.value,

  // Store state breakdown
  storeState: {
    isInitialized: store.getters['savedSearches/isInitialized'],
    savedSearchesListings: store.state.savedSearches?.savedSearchesListings?.length || 0,
    similarListings: store.state.savedSearches?.similarListings?.length || 0,
    lastFetch: store.state.savedSearches?.lastFetch
      ? new Date(store.state.savedSearches.lastFetch).toLocaleTimeString()
      : 'Never',
  },

  // Content breakdown
  breakdown: listingBreakdown.value,

  // User experience
  userExperience: {
    contentType: !isAuthenticated.value
      ? 'recommendations'
      : savedSearches.value.length > 0
      ? 'saved-searches'
      : 'recommendations',
    showingFallbackContent: !isAuthenticated.value && displayListings.value.length > 0,
    hasAnyContent: displayListings.value.length > 0,
  },
}));

const debugSavedSearches = () => {
  console.group('🔍 SavedSearchesComponent Debug');

  // Debug saved searches store state
  console.log('Store state:', {
    savedSearches: store.state.savedSearches?.savedSearches?.length || 0,
    savedSearchesListings: store.state.savedSearches?.savedSearchesListings?.length || 0,
    similarListings: store.state.savedSearches?.similarListings?.length || 0,
    displayListings: displayListings.value?.length || 0,
  });

  // Debug the actual data
  console.log('Saved searches:', savedSearches.value);
  console.log('Display listings:', displayListings.value);

  // Log the criteria used for shouldShowSection
  const savedSearchesLength = store.state.savedSearches?.savedSearches?.length || 0;
  const similarListingsLength = store.state.savedSearches?.similarListings?.length || 0;

  console.log('shouldShowSection calculation:', {
    hasSearches: savedSearchesLength > 0,
    hasSimilar: similarListingsLength > 0,
    result: savedSearchesLength > 0 || similarListingsLength > 0,
  });

  console.groupEnd();
};

// Call this function on mount and when displayListings changes
watch([displayListings, shouldShowSection], () => {
  debugSavedSearches();
});

onMounted(() => {
  console.log('🚀 SavedSearchesComponent mounted');
  debugSavedSearches();
});

// ✅ SIMPLIFIED: Methods
const refreshData = async () => {
  //console.log('🔄 Manual refresh triggered');
  await store.dispatch('savedSearches/refresh');
};

const retryInitialization = async () => {
  // console.log('🔄 Retrying saved searches initialization...');
  await store.dispatch('savedSearches/refresh');
};

const handleViewDetails = (listing) => {
  // console.log('🔍 Viewing listing details:', listing.id);
  emit('viewDetails', listing);
};

const handleToggleFavorite = (listing) => {
  // console.log('❤️ Toggling favorite for listing:', listing.id);
  emit('toggleFavorite', listing);
};

// ✅ SIMPLIFIED: Watch for authentication changes
watch(
  () => isAuthenticated.value,
  async (newAuth, oldAuth) => {
    if (newAuth !== oldAuth) {
      // console.log('👤 Authentication status changed:', { from: oldAuth, to: newAuth });
      // Refresh data when authentication changes
      await refreshData();
    }
  }
);

// ✅ SIMPLIFIED: Lifecycle
// onMounted(async () => {
//   console.log('🚀 SavedSearches component mounted');

//   try {
//      console.log('🔍 Initializing saved searches for all users...');
//     await store.dispatch('savedSearches/initialize');
//     console.log('✅ Saved searches initialized successfully');
//   } catch (err) {
//     console.error('❌ Failed to initialize saved searches:', err);
//   }
// });
</script>

<template>
  <div>
    <!-- ✅ ENHANCED: Debug panel with more insights -->
    <!-- <div v-if="debugMode" class="debug-panel pa-2 ma-2">
      <h4>🔍 Saved Searches Debug Panel</h4>
      
      <div class="debug-actions mb-3">
        <button @click="refreshData" class="debug-button mr-2">🔄 Refresh Data</button>
        <button @click="retryInitialization" class="debug-button">🔁 Retry Init</button>
      </div>
      
      <div class="debug-summary mb-3">
        <h5>📊 Quick Summary</h5>
        <div class="debug-stats">
          <span class="stat-item">
            <strong>User:</strong> {{ isAuthenticated ? 'Authenticated' : 'Guest' }}
          </span>
          <span class="stat-item">
            <strong>Content:</strong> {{ debugInfo.userExperience.contentType }}
          </span>
          <span class="stat-item">
            <strong>Listings:</strong> {{ displayListings.length }}
          </span>
          <span class="stat-item">
            <strong>Status:</strong> {{ isLoading ? 'Loading...' : 'Ready' }}
          </span>
        </div>
      </div>
      
      <details class="debug-details">
        <summary>🔧 Detailed Debug Info</summary>
        <pre class="debug-data">{{ JSON.stringify(debugInfo, null, 2) }}</pre>
      </details>
    </div> -->

    <!-- ✅ ENHANCED: Main content with better UX messaging -->
    <SlidingScrollableListingComponent
      v-if="shouldShowSection"
      :title="sectionTitle"
      titleClasses="saved-searches-title font-secondary text-lg font-semibold"
      :listings="displayListings"
      :loading="isLoading"
      :error="error"
      :favorites="favorites"
      :seeMoreText="isAuthenticated ? 'View all saved searches' : 'Explore more listings'"
      :emptyMessage="
        isAuthenticated
          ? 'Your saved searches are ready, but no matching listings found. Try browsing to see new listings.'
          : 'Discover personalized recommendations by browsing our listings.'
      "
      emptyActionText="Browse listings"
      :emptyActionLink="{ name: 'searchResults' }"
      sectionType="saved"
      @viewDetails="handleViewDetails"
      @toggleFavorite="handleToggleFavorite"
      @retry="retryInitialization"
    >
      <!-- ✅ ENHANCED: Smart subtitle showing content breakdown -->
      <template
        #subtitle
        v-if="listingBreakdown.hasSavedListings || listingBreakdown.hasSimilarListings"
      >
        <div class="listings-breakdown text-caption text-grey-600 mt-1">
          <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>

          <!-- For authenticated users with saved searches -->
          <span v-if="isAuthenticated && listingBreakdown.hasSavedListings">
            {{ listingBreakdown.savedSearchResults }} from saved searches
          </span>

          <!-- Separator -->
          <span
            v-if="
              isAuthenticated &&
              listingBreakdown.hasSavedListings &&
              listingBreakdown.hasSimilarListings
            "
          >
            •
          </span>

          <!-- Similar/recommended listings -->
          <span v-if="listingBreakdown.hasSimilarListings">
            {{ listingBreakdown.similarListings }}
            {{ isAuthenticated ? 'similar' : 'recommended' }} listings
          </span>

          <!-- For guests with only recommendations -->
          <span
            v-if="
              !isAuthenticated &&
              listingBreakdown.hasSimilarListings &&
              !listingBreakdown.hasSavedListings
            "
          >
            Personalized recommendations based on popular listings
          </span>
        </div>
      </template>
    </SlidingScrollableListingComponent>
  </div>
</template>

<style scoped>
/* ✅ ENHANCED: Better styling with dynamic colors */
.saved-searches-title {
  color: #1976d2;

  transition: color 0.3s ease;
}

.listings-breakdown {
  border-left: 3px solid #1976d2;
  padding-left: 8px;
  margin-top: 4px;
  font-size: var(--font-size-xs) !important; /* Reduced from 12px */
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.listings-breakdown:hover {
  opacity: 1;
}

.listings-breakdown .v-icon {
  color: #1976d2;
}

/* ✅ ENHANCED: Better debug panel styling */
.debug-panel {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  margin-bottom: 16px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.debug-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.debug-button {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  border: none;
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.debug-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.debug-button:active {
  transform: translateY(0);
}

.debug-summary {
  background: rgba(255, 255, 255, 0.8);
  padding: 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.debug-summary h5 {
  margin: 0 0 8px 0;
  color: #495057;
  font-size: 14px;
}

.debug-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.stat-item {
  font-size: 12px;
  padding: 4px 8px;
  background: rgba(25, 118, 210, 0.1);
  border-radius: 4px;
  color: #1976d2;
}

.debug-details {
  margin-top: 12px;
}

.debug-details summary {
  cursor: pointer;
  padding: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  font-weight: 500;
  color: #495057;
}

.debug-details summary:hover {
  background: rgba(255, 255, 255, 0.9);
}

.debug-data {
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
}

/* ✅ RESPONSIVE: Better mobile experience */

/* Media queries */
@media (max-width: 768px) {
  .listings-breakdown {
    font-size: var(--font-size-xs) !important; /* Kept at 11px */
  }
}

@media (max-width: 480px) {
  .listings-breakdown {
    font-size: 10px !important; /* Already small, keeping at 10px */
    padding-left: 6px;
  }
}
@media (max-width: 768px) {
  .saved-searches-title {
    font-size: 20px !important;
  }

  .debug-panel {
    padding: 12px;
    margin-bottom: 12px;
  }

  .debug-button {
    padding: 5px 10px;
    font-size: 11px;
  }

  .debug-stats {
    flex-direction: column;
    gap: 6px;
  }

  .listings-breakdown {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .saved-searches-title {
    font-size: 18px !important;
  }

  .debug-panel {
    padding: 10px;
    font-size: 10px;
  }

  .listings-breakdown {
    font-size: 10px;
    padding-left: 6px;
  }
}

/* ✅ ACCESSIBILITY: Better focus states */
.debug-button:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.debug-details summary:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* ✅ PRINT: Hide debug panel when printing */
@media print {
  .debug-panel {
    display: none;
  }
}
</style>
